import { computed, defineComponent, provide, inject, ref, watch, onMounted, onBeforeUnmount, } from "vue";
import { debounceTime, filter, tap } from "rxjs/operators";
import { rxInitSubjectCallback, useObservable } from "src/plugins/rxjs/base";
import { useViewStore } from "src/stores/view";
import useQuasar from 'quasar/src/composables/use-quasar/use-quasar.js';
import { AppGeneralVariables } from "src/plugins/general/variables";
import { KeyboardShowScrollFunctions } from "src/plugins/scroll/keyboardShowScroll";
import { v4 as uuidv4 } from "uuid";
import cloneDeep from "lodash.clonedeep";
export default defineComponent({
    name: "general-dialog",
    props: {
        customCardClass: {
            type: Object,
            default: () => {
                return {};
            },
        },
        closeBtnClass: {
            type: Object,
            default: () => {
                return {};
            },
        },
        closeBtnStyle: {
            type: Object,
            default: () => {
                return {};
            },
        },
        customColor: {
            type: Object,
            default: () => {
                return {};
            },
        },
        customSectionClass: {
            type: Object,
            default: () => {
                return {};
            },
        },
        customSectionStyle: {
            type: Object,
            default: () => {
                return {};
            },
        },
        customStickyColor: String,
        disableBasePadding: Boolean,
        disableSmoothScroll: Boolean,
        disableTransition: Boolean,
        fatHeader: Boolean,
        fullScreen: Boolean,
        hideBtn: Boolean,
        hideHeader: Boolean,
        hideHeaderSeparator: Boolean,
        hideTransition: {
            type: String,
            default: "slide-down",
        },
        leanHeader: Boolean,
        maximize: Boolean,
        modelValue: Boolean,
        persistent: Boolean,
        position: {
            type: String,
            default: 'standard'
        },
        separateBackClose: Boolean,
        showTransition: {
            type: String,
            default: "slide-up",
        },
        stickyHeaderStyle: {
            type: Object,
            default: () => {
                return {};
            },
        },
        title: String,
        width: String,
    },
    emits: [
        "back",
        "close",
        "scroll",
        "show",
        "update:modelValue"
    ],
    setup(props, context) {
        const $q = useQuasar();
        const viewStore = useViewStore();
        /**
         * General stuffs
         */
        const { styleType } = AppGeneralVariables();
        const dialogUniqueId = ref(uuidv4());
        const bgColor = computed(() => {
            return Object.keys(props.customColor).length > 0
                ? props.customColor
                : { "white-dark-background": true };
        });
        const closeBtnStandardClass = computed(() => {
            return {
                ...props.closeBtnClass,
                "white-grey8-text-color": Object.keys(props.customColor).length === 0,
                "text-white": Object.keys(props.customColor).length > 0,
                "absolute-right q-mr-sm": true,
            };
        });
        const closeBtnStandardStyle = computed(() => {
            return {
                ...props.closeBtnStyle,
                "font-weight": "bold !important",
            };
        });
        const dialogOpened = computed(() => viewStore.dialogOpened);
        const maximized = computed(() => props.fullScreen || (props.maximize &&
            (!$q.platform.is.desktop || $q.screen.lt.lg)));
        const toolbarHeader = computed(() => (styleType.value !== 'app' && !props.leanHeader) || props.fatHeader);
        const size = computed(() => {
            if (props.fullScreen || (props.maximize &&
                (!$q.platform.is.desktop || $q.screen.lt.lg))) {
                return "100vw";
            }
            else if ($q.screen.lt.sm) {
                return "95vw";
            }
            else if ($q.screen.lt.md) {
                return "80vw";
            }
            else if ($q.screen.lt.lg) {
                return "70vw";
            }
            return "350px";
        });
        /**
         * Scroll functions
         */
        const { subject: delayedScroll$ } = rxInitSubjectCallback();
        const { subject: renableScroll$ } = rxInitSubjectCallback();
        const cardRef = ref(null);
        function delayScrollTo(offset) {
            // scroll to with a debounce
            delayedScroll$.next(offset);
        }
        useObservable(delayedScroll$.pipe(debounceTime(500), tap((v) => {
            scrollTo(v);
        })));
        const scrollDisabled = ref(false);
        useObservable(renableScroll$.pipe(debounceTime(500), 
        // release the scroll lock after 0.5s
        tap(() => scrollDisabled.value = false)));
        function scrollTo(offset, fromResize = false) {
            if (!cardRef.value)
                return;
            if (!cardRef.value.$el)
                return;
            if (fromResize && scrollDisabled.value)
                return;
            cardRef.value.$el.scrollTop = offset;
            // only scroll to the first value provided. block subsequent requests for 0.5s
            // as resize observer for iOS can cause some unintended behaviour
            if (fromResize) {
                scrollDisabled.value = true;
                renableScroll$.next('');
            }
        }
        function scrollToCard(offset) {
            // when keyboard shows, fixate to current position
            const fixedPos = (offset || 0) + 50;
            if (!cardRef.value || !cardRef.value.$el) {
                scrollTo(fixedPos);
                return;
            }
            scrollTo(cardRef.value.$el.scrollTop || 0 +
                fixedPos);
        }
        function emitScroll(e) {
            // emit scroll event
            context.emit('scroll', e);
        }
        // detect keyboard show/hide
        const scrollLogic = computed(() => {
            return dialogOpened.value && dialogOpened.value.length > 0
                ? dialogOpened.value[dialogOpened.value.length - 1] === dialogUniqueId.value
                : false;
        });
        const { touchDetect, touchMove } = KeyboardShowScrollFunctions(scrollLogic, scrollTo, scrollToCard);
        const { subject: closeModal$ } = rxInitSubjectCallback();
        /**
         * Force close dialog. Handle dialog close functions from anywhere in the app
         */
        const forceCloseDialog = computed({
            get() {
                return viewStore.forceCloseDialog;
            },
            set(value) {
                viewStore.toggleForceCloseDialog(value);
            },
        });
        watch(forceCloseDialog, (newValue) => {
            if (!newValue)
                return;
            if (!showModal.value)
                return;
            closeModal$.next(false);
        });
        const dialogContentRef = ref(null);
        /**
         * Observe resize. To override the default focus scroll function in safari
         */
        function resizeObserverMethod() {
            if (!cardRef.value)
                return;
            if (!cardRef.value.$el)
                return;
            // prevent ios app from scrolling focus on the "show more" buttons
            scrollTo(cardRef.value.$el.scrollTop || 0, true);
        }
        const ro = ref(null);
        const { subject: setRO$ } = rxInitSubjectCallback();
        useObservable(setRO$.pipe(debounceTime(500), filter(() => ($q.platform.is.ios || false) && ($q.platform.is.capacitor || false)), filter(() => dialogContentRef.value !== null), tap(() => {
            if (!ro.value) {
                ro.value = new ResizeObserver(resizeObserverMethod);
            }
        }), tap(() => {
            ro.value?.observe(dialogContentRef.value);
        })));
        /**
         * Generic close dialog functions
         */
        const { subject: delayedShowModal$ } = rxInitSubjectCallback();
        const { subject: delayedCloseModal$ } = rxInitSubjectCallback();
        const delayedShowModal = ref(false);
        const showModal = computed({
            get() {
                return props.modelValue;
            },
            set(value) {
                context.emit('update:modelValue', value);
            },
        });
        watch(showModal, (newValue) => {
            if (!newValue)
                return;
            viewStore.openDialog(dialogUniqueId.value);
        }, { immediate: true });
        provide('isIntersectDialog', true);
        provide('isIntersectDialogShown', showModal);
        function closeModelFunc() {
            // must not send the signal to close dialog
            // if dialog is not opened to prevent unintended consequences
            if (!showModal.value)
                return;
            showModal.value = false;
        }
        function closeOnBack() {
            const lastDialogId = cloneDeep(dialogOpened.value).pop();
            if (!delayedShowModal.value || (lastDialogId !== dialogUniqueId.value))
                return;
            closeModal$.next(false);
        }
        function checkPersistence() {
            if (props.persistent)
                return;
            closeModelFunc();
        }
        useObservable(closeModal$.pipe(debounceTime(200), tap((v) => {
            if (!v && props.separateBackClose) {
                context.emit("back");
                return;
            }
            // must not send the signal to close dialog
            // if dialog is not opened to prevent unintended consequences
            if (!showModal.value)
                return;
            showModal.value = v;
        })));
        useObservable(delayedCloseModal$.pipe(debounceTime(500), tap(() => {
            if (showModal.value)
                return;
            viewStore.closeDialog(dialogUniqueId.value);
            forceCloseDialog.value = false;
            if (dialogContentRef.value &&
                $q.platform.is.ios && $q.platform.is.capacitor) {
                ro.value?.unobserve(dialogContentRef.value);
                ro.value = null;
            }
        })));
        useObservable(delayedShowModal$.pipe(debounceTime(300), tap((v) => {
            // delayedScroll$.next(0)
            delayedShowModal.value = v;
        })));
        watch(showModal, (newValue) => {
            delayedShowModal$.next(newValue);
        }, { immediate: true });
        /**
         * General house keeping for component mounted and before unmount
         */
        onMounted(() => {
            // detect back button
            if ($q.platform.is.capacitor) {
                document?.addEventListener("backbutton", closeOnBack, {
                    passive: true,
                    capture: false,
                });
            }
            else {
                window?.addEventListener("popstate", closeOnBack, {
                    passive: true,
                    capture: false,
                });
            }
        });
        onBeforeUnmount(() => {
            if ($q.platform.is.capacitor) {
                document?.removeEventListener("backbutton", closeOnBack, {
                    // @ts-expect-error ignore
                    passive: true,
                    capture: false,
                });
            }
            else {
                window?.removeEventListener("popstate", closeOnBack, {
                    // @ts-expect-error ignore
                    passive: true,
                    capture: false,
                });
            }
            viewStore.closeDialog(dialogUniqueId.value);
            if (dialogContentRef.value &&
                $q.platform.is.ios && $q.platform.is.capacitor) {
                ro.value?.unobserve(dialogContentRef.value);
            }
        });
        /**
         * Close app drawer on dialog click
         */
        const disableToggleDrawer = inject('disableToggleDrawer', false);
        function dialogClick() {
            if (disableToggleDrawer)
                return;
            viewStore.toggleDrawer(false);
        }
        watch(showModal, (newValue) => {
            if (newValue) {
                setRO$.next('');
                return;
            }
            // set dialog to close in store
            delayedCloseModal$.next("");
        });
        return {
            styleType,
            closeModal$,
            dialogContentRef,
            bgColor,
            cardRef,
            closeBtnStandardClass,
            closeBtnStandardStyle,
            delayedShowModal,
            dialogUniqueId,
            dialogOpened,
            maximized,
            size,
            showModal,
            toolbarHeader,
            closeModelFunc,
            closeOnBack,
            checkPersistence,
            delayScrollTo,
            dialogClick,
            emitScroll,
            resizeObserverMethod,
            scrollTo,
            touchDetect,
            touchMove,
        };
    },
});
